import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const ICONS = {
  interest_received: "❤",
  interest_accepted: "✓",
  interest_declined: "×",
  payment_approved: "★",
  payment_rejected: "!",
  admin_new_payment: "₹",
};

/** Bell icon in the navbar. Polls every 30s for unread count and lazily
 * fetches the full list when the dropdown opens. */
export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef();

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 30000);
    return () => clearInterval(t);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openDropdown = async () => {
    setOpen((v) => !v);
    if (!items) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
      } catch {
        setItems([]);
      }
    }
  };

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setCount((c) => Math.max(0, c - 1));
    } catch {
      // ignore
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => (prev || []).map((x) => ({ ...x, read: true })));
      setCount(0);
    } catch {
      // ignore
    }
  };

  return (
    <div className="sb-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="sb-bell"
        onClick={openDropdown}
        aria-label="Notifications"
        data-testid="nav-bell"
      >
        <span aria-hidden>🔔</span>
        {count > 0 && (
          <span className="sb-bell-badge" data-testid="bell-unread-count">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="sb-bell-dropdown" data-testid="bell-dropdown">
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <strong>Notifications</strong>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 sb-link-quiet"
              onClick={markAll}
              data-testid="bell-mark-all"
            >
              Mark all read
            </button>
          </div>
          {items === null ? (
            <div className="px-3 py-3 text-muted small">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-4 text-center text-muted small" data-testid="bell-empty">
              You're all caught up.
            </div>
          ) : (
            <ul className="list-unstyled mb-0" style={{ maxHeight: 380, overflow: "auto" }}>
              {items.slice(0, 20).map((n) => (
                <li key={n.id} className={"sb-bell-item" + (n.read ? " read" : "")}>
                  <Link
                    to={n.ref_url || "/dashboard"}
                    onClick={() => {
                      markRead(n);
                      setOpen(false);
                    }}
                    data-testid={`bell-item-${n.id}`}
                  >
                    <span className="sb-bell-icon" aria-hidden>
                      {ICONS[n.type] || "•"}
                    </span>
                    <span className="sb-bell-body">
                      <span className="sb-bell-title">{n.title}</span>
                      {n.body && <span className="sb-bell-sub">{n.body}</span>}
                      <span className="sb-bell-time">
                        {new Date(n.created_at).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
